import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import OxigenGraph from "../../icons/OxigenGraph";
import faTranslations from "../locales/HealthMonitoring-fa.json";
import enTranslations from "../locales/HealthMonitoring-en.json";
import arTranslations from "../locales/HealthMonitoring-ar.json";
import trTranslations from "../locales/HealthMonitoring-tr.json";
const OxygenLevelGraph: React.FC = () => {
    const { t, i18n } = useTranslation("healthMonitoring");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [isExpanded, setIsExpanded] = useState(false);

    const oxygenData = [97, 98, 98, 99, 98, 97, 98, 98];
    const timeLabels = [
        t("graphs.oxygen.times.0"),
        t("graphs.oxygen.times.1"),
        t("graphs.oxygen.times.2"),
        t("graphs.oxygen.times.3"),
        t("graphs.oxygen.times.4"),
        t("graphs.oxygen.times.5"),
        t("graphs.oxygen.times.6"),
        t("graphs.oxygen.times.7"),
        t("graphs.oxygen.times.8"),
    ];

    return (
        <div className="mt-4 relative" dir={isRTL ? "rtl" : "ltr"}>
            {/* Central Display */}
            <motion.div className="relative flex items-center justify-center mb-6 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <div className="relative z-10 bg-gradient-to-br from-[#00E5FF]/20 to-[#00E5FF]/5 rounded-full w-[120px] h-[120px] flex flex-col items-center justify-center shadow-lg border border-[#00E5FF]/20">
                    <div className="text-4xl font-bold bg-gradient-to-r from-[#00E5FF] to-[#0072CE] bg-clip-text text-transparent">{t("graphs.oxygen.currentLevel")}</div>
                    <div className="text-sm text-[#00E5FF]/80">SpO₂%</div>
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-white/90 text-[10px] text-[#00E5FF] px-2 py-0.5 rounded-full shadow-sm border border-[#00E5FF]/10">
                        {t("graphs.oxygen.status.excellent")}
                    </div>
                </div>
            </motion.div>

            {/* Oxygen Level Indicators */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-gradient-to-br px-5 from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                    <div className="text-xs text-[#9EA3A9]">{t("graphs.oxygen.min")}</div>
                    <div className="text-sm font-medium text-[#00E5FF]">{t("graphs.oxygen.minValue")}</div>
                </div>
                <div className="bg-gradient-to-br px-5 from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                    <div className="text-xs text-[#9EA3A9]">{t("graphs.oxygen.average")}</div>
                    <div className="text-sm font-medium text-[#00E5FF]">{t("graphs.oxygen.averageValue")}</div>
                </div>
                <div className="bg-gradient-to-br px-5 from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                    <div className="text-xs text-[#9EA3A9]">{t("graphs.oxygen.max")}</div>
                    <div className="text-sm font-medium text-[#00E5FF]">{t("graphs.oxygen.maxValue")}</div>
                </div>
            </div>

            {/* Saturation Range Display */}
            <div className="relative">
                <div className={`text-xs text-[#9EA3A9] mb-3 flex flex-row justify-between items-center`}>
                    <span>{t("graphs.oxygen.saturationRange")}</span>
                    <span className={`text-[#32D74B] flex items-center flex-row`}>
                        <svg className={`w-3 h-3 ${isRTL ? "mr-1" : "ml-1"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        {t("graphs.oxygen.status.normal")}
                    </span>
                </div>

                <div className="h-12 w-full bg-[#ECEFF1]/50 rounded-xl overflow-hidden relative">
                    <div className={`absolute top-0 ${isRTL ? "right-0 left-0" : "left-0 right-0"} h-full flex items-center px-3`}>
                        <div className="relative w-full h-2 bg-[#ECEFF1] rounded-full overflow-hidden">
                            <div className={`absolute top-0 ${isRTL ? "right-[70%] left-0" : "left-[70%] right-0"} h-full bg-gradient-to-r from-[#00E5FF]/50 to-[#00E5FF]`}></div>
                            <div className={`absolute top-0 h-full w-2 bg-white rounded-full shadow-md border border-[#00E5FF]/30`} style={{ [isRTL ? "right" : "left"]: "85%" }} />
                        </div>
                    </div>

                    <div className={`absolute bottom-1 ${isRTL ? "right-0 left-0" : "left-0 right-0"} flex flex-row justify-between px-3`}>
                        <span className="text-[10px] text-[#9EA3A9]">{t("graphs.oxygen.rangeMin")}</span>
                        <span className="text-[10px] text-[#9EA3A9]">{t("graphs.oxygen.rangeMid")}</span>
                        <span className="text-[10px] text-[#9EA3A9]">{t("graphs.oxygen.rangeMax")}</span>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="mt-4">
                        <div className={`text-xs text-[#9EA3A9] mb-1 text-start`}>{t("graphs.oxygen.todayChart")}</div>
                        <div className="h-32 bg-[#ECEFF1]/50 rounded-xl p-3 relative">
                            {/* Grid Lines */}
                            <div className="absolute inset-0 flex flex-col justify-between p-3 pointer-events-none">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="w-full h-px bg-[#9EA3A9]/10"></div>
                                ))}
                            </div>

                            {/* Oxygen Chart */}
                            <div className="relative h-full w-full flex items-end">
                                <OxigenGraph oxygenData={oxygenData} />

                                {/* Time Labels */}
                                <div
                                    className={`absolute bottom-0 ${isRTL ? "right-0 left-0" : "left-0 right-0"} flex ${
                                        isRTL ? "flex-row-reverse" : "flex-row"
                                    } justify-between text-[10px] text-[#9EA3A9]`}
                                >
                                    {timeLabels.map((time, i) => (
                                        <span key={i} className={i % 2 === 0 ? "" : "invisible md:visible"}>
                                            {time}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Health Insights */}
                        <div className="mt-3 p-3 bg-gradient-to-r from-[#00E5FF]/10 to-[#00E5FF]/5 rounded-xl">
                            <div className={`text-xs font-medium text-[#00E5FF] mb-1 text-start`}>{t("graphs.oxygen.insights.title")}</div>
                            <div className={`text-xs text-[#002B5C] text-start`}>{t("graphs.oxygen.insights.text")}</div>
                        </div>

                        {/* Breathing Exercise Tool */}
                        <div className="mt-3 p-3 bg-[#F5F7FA] rounded-xl">
                            <h4 className={`text-sm font-medium text-[#002B5C] mb-2 text-start`}>{t("graphs.oxygen.breathingExercise.title")}</h4>
                            <p className={`text-xs text-[#4A5568] mb-3 text-start`}>{t("graphs.oxygen.breathingExercise.description")}</p>

                            <div className="relative h-20 bg-[#ECEFF1]/50 rounded-xl overflow-hidden">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="text-sm font-medium text-[#002B5C]">{t("graphs.oxygen.breathingExercise.inhale")}</div>
                                        <div className="text-xs text-[#9EA3A9]">{t("graphs.oxygen.breathingExercise.inhaleTime")}</div>
                                    </div>
                                </div>

                                <div className={`absolute bottom-2 ${isRTL ? "right-0 left-0" : "left-0 right-0"} flex flex-row justify-between px-4`}>
                                    <div className="text-center">
                                        <div className="text-[10px] font-medium text-[#002B5C]">{t("graphs.oxygen.breathingExercise.hold")}</div>
                                        <div className="text-[10px] text-[#9EA3A9]">{t("graphs.oxygen.breathingExercise.holdTime")}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-[10px] font-medium text-[#002B5C]">{t("graphs.oxygen.breathingExercise.exhale")}</div>
                                        <div className="text-[10px] text-[#9EA3A9]">{t("graphs.oxygen.breathingExercise.exhaleTime")}</div>
                                    </div>
                                </div>

                                <div className="absolute bottom-0 left-0 w-full h-1 bg-[#00E5FF]/30">
                                    <div className="h-full bg-[#00E5FF] animate-pulse" style={{ width: "25%" }}></div>
                                </div>
                            </div>

                            <div className="flex justify-center mt-3">
                                <button className="bg-[#00E5FF]/20 text-[#00E5FF] text-xs font-medium px-4 py-2 rounded-full">{t("graphs.oxygen.breathingExercise.startButton")}</button>
                            </div>
                        </div>

                        {/* Weekly Trend */}
                        <div className="mt-3">
                            <div className={`text-xs text-[#9EA3A9] mb-1 text-start`}>{t("graphs.oxygen.weeklyTrend")}</div>
                            <div className="h-24 bg-[#ECEFF1]/50 rounded-xl p-3 relative">
                                <div className="flex items-end justify-between h-full">
                                    {[97, 98, 97, 98, 99, 98, 98].map((value, index) => (
                                        <div key={index} className="flex flex-col items-center">
                                            <div className="w-4 bg-gradient-to-t from-[#00E5FF] to-[#00E5FF]/70 rounded-t-sm" style={{ height: `${(value - 90) * 10}%` }}></div>
                                            <div className="text-[9px] text-[#9EA3A9] mt-1">{t(`graphs.oxygen.weekDays.${index}`)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default OxygenLevelGraph;
